import { useEffect, useState } from 'react';
import { useRxCollection } from 'rxdb-hooks';
import { v4 as uuidv4 } from 'uuid';

import { useMst } from '../../mobx-models/Root';
import { Deficiency } from '../../models/models';
import { ComponentInfo, InspectorRole, TaskStatus } from '../../utilities/enums';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { isNull } from '../../utilities/utilityFunctions';
import { RxdbCollectionName } from '../rxdbCollectionName';
import type { TaskList } from '../taskList/taskList.types';
import { useTaskList } from '../taskList/useTaskList';
import type { DeficiencyList, DeficiencyListCollection } from './deficiencyList.types';

export const useDeficiencyList = () => {
  const { SiteDiscipline, CurrentUserSelected } = useMst();
  const deficiencyListCollection: DeficiencyListCollection = useRxCollection(
    RxdbCollectionName.DEFICIENCY_LIST,
  );

  const [deficiencyListInitialized, setdeficiencyListInitialized] = useState<boolean>(false);
  const { taskListCollection } = useTaskList();

  useEffect(() => {
    if (deficiencyListCollection) setdeficiencyListInitialized(true);
  }, [deficiencyListCollection]);

  const saveDeficiencyList = async (deficiencyPayload: DeficiencyList) => {
    try {
      const task = await taskListCollection
        ?.findOne({ selector: { pkAssetInspectionTask: CurrentUserSelected.taskId } })
        .exec();
      const roleId =
        task?.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL ||
        task?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING
          ? InspectorRole.LEAD
          : CurrentUserSelected?.currentUser?.clerkId
          ? InspectorRole.DEC
          : InspectorRole.FIELD;

      // Note: delete deficiencyPayload.isChanged;
      const deficiencyRecord: DeficiencyList = {
        name: isNull(deficiencyPayload, ComponentInfo.NAME, ''),
        pkDeficiencyUUID: isNull(deficiencyPayload, ComponentInfo.PK_DEFICIENCY_UUID, uuidv4()),
        repairFactorId: isNull(deficiencyPayload, ComponentInfo.REPAIR_FACTOR_ID, null),
        inspectionTaskId: CurrentUserSelected.taskId,
        isCompletedDuringInspection: isNull(
          deficiencyPayload,
          ComponentInfo.IS_COMPLETED_DURING_INSPECTION,
          null,
        ),
        componentType: isNull(deficiencyPayload, ComponentInfo.COMPONENT_TYPE, ''),
        remedialAction: isNull(deficiencyPayload, ComponentInfo.REMEDIAL_ACTION, ''),
        causeOfFailure: isNull(deficiencyPayload, ComponentInfo.CAUSE_OF_FAILURE, null),
        remedialActionId: isNull(deficiencyPayload, ComponentInfo.REMEDIAL_ACTION_ID, 0),
        commentRemedialAction: isNull(
          deficiencyPayload,
          ComponentInfo.COMMENT_REMEDIAL_ACTION,
          null,
        ),
        assetCategoryComponentId: isNull(
          deficiencyPayload,
          ComponentInfo.ASSET_CATEGORY_COMPONENT_ID,
          0,
        ),
        commentAssetComponent: isNull(
          deficiencyPayload,
          ComponentInfo.COMMENT_ASSET_COMPONENT,
          null,
        ),
        commentInspectorLead: isNull(deficiencyPayload, ComponentInfo.COMMENT_INSPECTOR_LEAD, null),
        commentInspectorField: isNull(
          deficiencyPayload,
          ComponentInfo.COMMENT_INSPECTOR_FIELD,
          null,
        ),
        attentionFlag: isNull(deficiencyPayload, ComponentInfo.ATTENTION_FLAG, false),
        failureCauseCategory: isNull(deficiencyPayload, ComponentInfo.FAILURE_CAUSE_CATEGORY, null),
        locationDescription: isNull(deficiencyPayload, ComponentInfo.LOCATION_DESCRIPTION, null),
        commentObservation: isNull(deficiencyPayload, ComponentInfo.COMMENT_OBSERVATION, null),
        dateObserved: isNull(
          deficiencyPayload,
          ComponentInfo.DATE_OBSERVED,
          new Date().toISOString(),
        ),
        componentFailureId: isNull(deficiencyPayload, ComponentInfo.COMPONENT_FAILURE_ID, '0'),
        componentAreaInfo: isNull(deficiencyPayload, ComponentInfo.COMPONENT_AREA_INFO, []),
        isActive: true,
        isDelete: isNull(deficiencyPayload, ComponentInfo.IS_DELETE, 'False'),
        assetInspectionId: deficiencyPayload?.isReInspectedComponent
          ? deficiencyPayload.assetInspectionId
          : CurrentUserSelected.inspectionId,
        site: SiteDiscipline.siteId,
        discipline: SiteDiscipline.disciplineId,
        fkComponent: isNull(deficiencyPayload, ComponentInfo.FK_COMPONENT, null),
        lowerElevation: isNull(deficiencyPayload, ComponentInfo.LOWER_ELEVATION, null),
        upperElevation: isNull(deficiencyPayload, ComponentInfo.UPPER_ELEVATION, null),
        isReInspectedComponent: isNull(
          deficiencyPayload,
          ComponentInfo.IS_REINSPECTED_COMPONENT,
          null,
        ),
        componentVersionId: isNull(deficiencyPayload, ComponentInfo.COMPONENT_VERSION_ID, null),
        inspectorOperationalDowntime: isNull(
          deficiencyPayload,
          ComponentInfo.OPERTION_DOWNTIME,
          null,
        ),
        inspectorUrgency: isNull(deficiencyPayload, ComponentInfo.URGENCY, null),
        inspectorTrafficScore: isNull(deficiencyPayload, ComponentInfo.TRAFFIC_SAFETY_SCORE, null),
        inspectorConditionRating: isNull(deficiencyPayload, ComponentInfo.CONDITION_RATING, null),
        componentVersionInspectionId: isNull(
          deficiencyPayload,
          ComponentInfo.COMPONENT_VERSION_INSPECTION_ID,
          null,
        ),
        componentVersionUUID: isNull(
          deficiencyPayload,
          ComponentInfo.COMPONENT_VERSION_UUID,
          uuidv4(),
        ),
        isAdhoc: isNull(deficiencyPayload, ComponentInfo.IS_ADHOC, null),
        fkAsset: isNull(deficiencyPayload, 'fkAsset', null),
        fkArea: isNull(deficiencyPayload, 'fkArea', null),
        fieldInspectorId: task?.fkInspectorField || 0,
        leadInspectorId: task?.fkInspectorLead || 0,
        taskStatusCategory:
          roleId === InspectorRole.LEAD ? TaskStatus.LI_PENDING_APPROVAL : TaskStatus.IN_PROGRESS,
      };

      let result = await deficiencyListCollection
        ?.findOne({
          selector: {
            componentVersionUUID: deficiencyRecord.componentVersionUUID,
          },
        })
        .update({
          $set: {
            name: deficiencyRecord.name,
            repairFactorId: deficiencyRecord.repairFactorId,
            isCompletedDuringInspection: deficiencyRecord.isCompletedDuringInspection,
            componentType: deficiencyRecord.componentType,
            remedialAction: deficiencyRecord.remedialAction,
            causeOfFailure: deficiencyRecord.causeOfFailure,
            remedialActionId: deficiencyRecord.remedialActionId,
            commentRemedialAction: deficiencyRecord.commentRemedialAction,
            assetCategoryComponentId: deficiencyRecord.assetCategoryComponentId,
            commentAssetComponent: deficiencyRecord.commentAssetComponent,
            commentInspectorLead: deficiencyRecord.commentInspectorLead,
            commentInspectorField: deficiencyRecord.commentInspectorField,
            attentionFlag: deficiencyRecord.attentionFlag,
            failureCauseCategory: deficiencyRecord.failureCauseCategory,
            locationDescription: deficiencyRecord.locationDescription,
            commentObservation: deficiencyRecord.commentObservation,
            dateObserved: deficiencyRecord.dateObserved,
            componentAreaInfo: deficiencyRecord.componentAreaInfo,
            isDelete: deficiencyRecord.isDelete,
            lowerElevation: deficiencyRecord.lowerElevation,
            upperElevation: deficiencyRecord.upperElevation,
            fkComponent: deficiencyRecord.fkComponent,
            isReInspectedComponent: deficiencyRecord.isReInspectedComponent,
            isActive: deficiencyRecord.isActive,
            componentVersionId: deficiencyRecord.componentVersionId,
            inspectorOperationalDowntime: deficiencyRecord.inspectorOperationalDowntime,
            inspectorConditionRating: deficiencyRecord.inspectorConditionRating,
            inspectorUrgency: deficiencyRecord.inspectorUrgency,
            inspectorTrafficScore: deficiencyRecord.inspectorTrafficScore,
            componentVersionInspectionId: deficiencyRecord.componentVersionInspectionId,
            componentVersionUUID: deficiencyPayload.componentVersionUUID,
            isAdhoc: deficiencyPayload.isAdhoc,
            fkArea: deficiencyPayload.fkArea,
            fkAsset: deficiencyPayload.fkAsset,
            role: roleId,
            fieldInspectorId: deficiencyRecord?.fieldInspectorId || 0,
            leadInspectorId: deficiencyRecord?.leadInspectorId || 0,
            taskStatusCategory: deficiencyRecord?.taskStatusCategory || 0,
          },
        });
      if (result === null) {
        result = await deficiencyListCollection?.insert({ ...deficiencyRecord, role: roleId });
      }
      return result;
    } catch (error) {
      console.log('🚀 ~ file: useDeficiencyList.ts ~ line 170 ~ saveDeficiencyList ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useDeficiencyList.tsx ~ line 125 ~ saveDeficiencyList ~',
      );
    }
  };

  const updateDeficiencyComment = async (
    componentFailureId: string,
    commentInspectorField: string,
    attentionFlag: boolean,
  ) => {
    try {
      if (!taskListCollection || !deficiencyListCollection) return;
      const task: TaskList | null = await taskListCollection
        ?.findOne({ selector: { pkAssetInspectionTask: CurrentUserSelected.taskId } })
        ?.exec();
      const role =
        task?.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL ||
        task?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING
          ? InspectorRole.LEAD
          : InspectorRole.FIELD;

      const result: DeficiencyList | null = await deficiencyListCollection
        .findOne({ selector: { componentFailureId } })
        .update({
          $set: {
            commentInspectorField,
            attentionFlag,
            role,
            taskStatusCategory:
              role === InspectorRole.LEAD ? TaskStatus.LI_PENDING_APPROVAL : TaskStatus.IN_PROGRESS,
            fieldInspectorId: task?.fkInspectorField || 0,
            leadInspectorId: task?.fkInspectorLead || 0,
          },
        });
      return result;
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useDeficiencyList.tsx ~ line 170 ~ updateDeficiencyComment ~',
      );
    }
  };

  const updateActiveDeficiencyComments = async (deficiencyPayload: Deficiency) => {
    try {
      if (!taskListCollection || !deficiencyListCollection) return;
      const task: TaskList | null = await taskListCollection
        ?.findOne({ selector: { pkAssetInspectionTask: CurrentUserSelected.taskId } })
        ?.exec();
      const role =
        task?.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL ||
        task?.fkInspectionTaskStatusCategory === TaskStatus.LI_SIGN_PENDING
          ? InspectorRole.LEAD
          : InspectorRole.FIELD;

      const deficiencyRecord: DeficiencyList = {
        name: deficiencyPayload.name,
        pkDeficiencyUUID: deficiencyPayload.pkDeficiencyUUID,
        repairFactorId: deficiencyPayload.repairFactorId,
        inspectionTaskId: CurrentUserSelected.taskId,
        isCompletedDuringInspection: deficiencyPayload.isCompletedDuringInspection,
        componentType: deficiencyPayload.componentType,
        remedialAction: deficiencyPayload.remedialAction,
        causeOfFailure: deficiencyPayload.causeOfFailure,
        remedialActionId: deficiencyPayload.remedialActionId,
        commentRemedialAction: deficiencyPayload.commentRemedialAction,
        assetCategoryComponentId: deficiencyPayload.assetCategoryComponentId,
        commentAssetComponent: deficiencyPayload.commentAssetComponent,
        commentInspectorLead: deficiencyPayload.commentInspectorLead,
        commentInspectorField: deficiencyPayload.commentInspectorField,
        attentionFlag: deficiencyPayload.attentionFlag,
        failureCauseCategory: deficiencyPayload.failureCauseCategory,
        locationDescription: deficiencyPayload.locationDescription,
        commentObservation: deficiencyPayload.commentObservation,
        dateObserved: deficiencyPayload.dateObserved,
        componentFailureId: deficiencyPayload.componentFailureId,
        componentAreaInfo: deficiencyPayload.componentAreaInfo,
        isActive: deficiencyPayload.isActive,
        isDelete: deficiencyPayload.isDelete,
        assetInspectionId: deficiencyPayload.assetInspectionId,
        site: SiteDiscipline.siteId,
        discipline: SiteDiscipline.disciplineId,
        fkComponent: deficiencyPayload.fkComponent,
        lowerElevation: deficiencyPayload.lowerElevation,
        upperElevation: deficiencyPayload.upperElevation,
        isReInspectedComponent: deficiencyPayload.isReInspectedComponent,
        componentVersionId: deficiencyPayload.componentVersionId,
        inspectorOperationalDowntime: deficiencyPayload.inspectorOperationalDowntime,
        inspectorUrgency: deficiencyPayload.inspectorUrgency,
        inspectorTrafficScore: deficiencyPayload.inspectorTrafficScore,
        inspectorConditionRating: deficiencyPayload.inspectorConditionRating,
        componentVersionInspectionId: deficiencyPayload.componentVersionInspectionId,
        componentVersionUUID: deficiencyPayload.componentVersionUUID,
        isAdhoc: deficiencyPayload.isAdhoc,
        fkAsset: deficiencyPayload.fkAsset,
        fkArea: deficiencyPayload.fkArea,
        fieldInspectorId: task?.fkInspectorField || 0,
        leadInspectorId: task?.fkInspectorLead || 0,
        taskStatusCategory:
          role === InspectorRole.LEAD ? TaskStatus.LI_PENDING_APPROVAL : TaskStatus.IN_PROGRESS,
        role,
      };

      let result: DeficiencyList | null = await deficiencyListCollection
        .findOne({
          selector: {
            componentVersionUUID: deficiencyPayload?.componentVersionUUID,
          },
        })
        .update({
          $set: {
            commentInspectorField: deficiencyPayload.commentInspectorField,
            attentionFlag: deficiencyPayload.attentionFlag,
            componentVersionInspectionId: deficiencyPayload?.componentVersionInspectionId,
            componentVersionId: deficiencyPayload?.componentVersionId,
            inspectionTaskId: deficiencyRecord.inspectionTaskId,
            fieldInspectorId: deficiencyRecord.fieldInspectorId,
            leadInspectorId: deficiencyRecord.leadInspectorId,
            taskStatusCategory: deficiencyRecord?.taskStatusCategory,
            role: deficiencyRecord.role,
          },
        });
      if (result === null) {
        result = await deficiencyListCollection?.insert({ ...deficiencyRecord, role });
      }
      return result;
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useDeficiencyList.tsx ~ line 170 ~ updateDeficiencyComment ~',
      );
    }
  };

  const deleteDeficiency = async (componentVersionUUID: string) => {
    try {
      let roleId;
      if (taskListCollection) {
        const isSameStatus: TaskList | null = await taskListCollection
          ?.findOne({ selector: { pkAssetInspectionTask: CurrentUserSelected.taskId } })
          .exec();
        roleId =
          isSameStatus?.fkInspectionTaskStatusCategory === TaskStatus.LI_PENDING_APPROVAL
            ? InspectorRole.LEAD
            : CurrentUserSelected?.currentUser?.clerkId
            ? InspectorRole.DEC
            : InspectorRole.FIELD;
      }
      if (deficiencyListCollection) {
        const result = await deficiencyListCollection
          ?.findOne()
          .where(ComponentInfo.COMPONENT_VERSION_UUID)
          .eq(componentVersionUUID)
          .update({
            $set: {
              isDelete: 'True',
              role: roleId,
            },
          });
        return result;
      }
    } catch (error) {
      sentryCaptureException(
        error,
        '~ file: useDeficiencyList.tsx ~ line 185 ~ deleteDeficiency ~',
      );
    }
  };

  return {
    deficiencyListInitialized,
    deficiencyListCollection,
    saveDeficiencyList,
    updateDeficiencyComment,
    deleteDeficiency,
    updateActiveDeficiencyComments,
  };
};
